// @ts-ignore
import { html, TemplateResult, CSSResultGroup } from 'lit';
// @ts-ignore
import { customElement, property, state, query } from 'lit/decorators.js';
import componentStyles from './idb-video-card.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
// @ts-ignore
import { classMap } from 'lit/directives/class-map.js';
import {SlotController} from "../../controllers/slot-controller";
import {SlotsController} from "../../controllers/slots-controller";


/**
 * The IDB VideoCard component
 *
 * This component allows you to display videos
 * using the HTML video tag
 *
 * @element idb-video-card
 */

@customElement('idb-video-card')
export class IdbVideoCard extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  slots = new SlotController(this, false);
  slotsFancy = new SlotsController(this);

  @query('video')
  videoElement!: HTMLVideoElement;

  @property({ type: Boolean, reflect: true, attribute: 'video-autoplay' })
  videoAutoplay: boolean;

  @property({ type: Boolean, reflect: true, attribute: 'video-muted' })
  videoMuted: boolean;

  @property({ type: Boolean, reflect: true, attribute: 'play-in-place' })
  playInPlace: boolean;

  @property({ type: String, reflect: true, attribute: 'video-url' })
  url: string;

  @property({ type: String, reflect: true, attribute: 'video-src' })
  source: string;

  @property({ type: String, reflect: true, attribute: 'video-thumb' })
  videoThumb: string;

  @state() videoThumbState: boolean;
  @state() hasVideoSlot: boolean;

  private intersectionObserver?: IntersectionObserver;

  firstUpdated() {
    this.hasVideoSlot = this.slots.test('sources');
    this.autoplayAttribute();
    this.mutedAttribute();
    this.posterAttribute();
    this.srcAttribute();

    this.videoIntersectionObserver();
  }

  disconnectedCallback() {
    this.intersectionObserver?.disconnect();
  }

  render(): TemplateResult {
    return html`
      ${this.videoThumb ? this.overlayTemplate() : null}
      ${this.videoPlayerTemplate()}
    `;
  }

  overlayTemplate(): TemplateResult|null {
    const overlayClasses = {
      'overlay': true,
    };
    const contentClasses = {
      'overlay-content': true,
    };

    const overlayContent =  html`
      <div
        class="${classMap(overlayClasses)}"
        style="background-image: url('${this.videoThumb}'); display: ${this.videoThumbState  ? 'none' : 'block'};"
      >
        <div class="${classMap(contentClasses)}">
          <button @click="${this.playInPlace ? this.playVideo : null}">
            <idb-icon icon="play-circle" stroke-width="2"></idb-icon>
          </button>
        </div>
      </div>
    `;

    return this.playInPlace
      ? html`${overlayContent}`
      : html`<a href="${this.url}" target="_blank">${overlayContent}</a>`
  }

  videoPlayerTemplate(): TemplateResult|null {
    const videoClasses = {
      'video-container': true,
    };

    const videoContent = html`
      <div class="${classMap(videoClasses)}">
        <video controls loop width="640px">
          ${this.slotsFancy.renderInShadow('sources')}
        </video>
      </div>
    `;

    return this.playInPlace ? html`${videoContent}` : null;
  }

  autoplayAttribute (): void {
    if (this.videoAutoplay) {
      this.videoElement.setAttribute('autoplay', '');
    }
  }

  mutedAttribute (): void {
    if (this.videoAutoplay || this.videoMuted) {
      this.videoElement.muted = true;
    }
  }

  posterAttribute (): void {
    if (this.videoThumb) {
      this.videoElement.poster = this.videoThumb;
    }
  }

  srcAttribute (): void {
    if (this.source) {
      this.videoElement.src = this.source;
    }
  }

  playVideo() {
    this.videoThumbState = !this.videoThumbState;
    this.videoElement.play().catch(console.error);
  }

  videoIntersectionObserver() {
    if (!this.videoElement) {
      return;
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (this.videoAutoplay) {
              this.videoElement.play().catch(console.error);
            }
          } else {
            this.videoElement.pause();
          }
        });
      },
      {
        threshold: 0.75,
        rootMargin: "0px",
        root: null
      }
    );

    this.intersectionObserver.observe(this.videoElement);
  }
}
